import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartTypeRegistry,
  LineController,
  BarController,
  PieController,
  DoughnutController,
} from 'chart.js'
import clsx from 'clsx'
import throttle from 'lodash/throttle'
import React, { useEffect, useRef, useState } from 'react'
import { Chart as ReactChart } from 'react-chartjs-2'

import Headings from '../../atoms/Heading'
import Paragraph from '../../atoms/Paragraph'
import { colorSet } from '../ChartsContainer/index.data'

// Register Chart Component
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
  PieController,
  DoughnutController,
)

interface props {
  type?: string
  data?: any
  isPercentageValue?: boolean
}

const ALLOWED_CHART_TYPE = ['bar', 'barStacked', 'barColoured', 'line', 'pie', 'doughnut']

const Charts = ({ type, data, isPercentageValue }: props) => {
  const chartRef = useRef<ChartJS>()
  const [options, setOptions] = useState({})
  const stacked = {
    stacked: type === 'barStacked',
  }

  useEffect(() => {
    if (type === 'doughnut' || type === 'pie') {
      setOptions({
        maintainAspectRatio: true,
        cutout: '75%',
        plugins: {
          legend: {
            display: false,
            position: 'right',
            labels: {
              padding: 15,
              usePointStyle: true,
            },
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw
                return isPercentageValue ? ` ${value}%` : value
              },
              labelPointStyle: function () {
                return {
                  pointStyle: 'circle',
                }
              },
            },
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: false,
          },
        },
      })
    } else {
      setOptions({
        layout: {
          padding: {
            right: 20,
            left: 20,
          },
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              padding: 15,
              usePointStyle: true,
            },
          },
          title: { display: false },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw
                return isPercentageValue ? ` ${value}%` : value
              },
              labelPointStyle: function () {
                return {
                  pointStyle: 'circle',
                }
              },
            },
          },
        },
        scales: {
          x: {
            ...stacked,
            grid: {
              display: false,
            },
            ticks: {
              color: '#000',
              font: {
                size: 16,
              },
            },
          },
          y: {
            ...stacked,
            ticks: {
              color: '#000',
              font: {
                size: 16,
              },
              callback: function (value) {
                return isPercentageValue ? value + '%' : value
              },
            },
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  useEffect(() => {
    const resizeChart = () => {
      chartRef.current?.resize()
    }

    const throttleResizeChart = throttle(resizeChart, 300, { leading: false })
    window.addEventListener('resize', throttleResizeChart)

    return () => {
      window.removeEventListener('resize', throttleResizeChart)
    }
  }, [])

  const applyMinWidth = type !== 'pie' && type !== 'doughnut'

  const mapChartData = (chart) => {
    if (!chart || !chart.datasets || !Array.isArray(chart.datasets)) {
      return {
        labels: [],
        datasets: [],
      }
    }

    return {
      labels: chart.labels,
      datasets: chart.datasets.map((item, index) => {
        return {
          label: item.label,
          data: item.data.map((value) => {
            return parseFloat(value.toString().replace('%', ''))
          }),
          borderColor: type === 'doughnut' || type === 'pie' || type === 'barColoured' ? colorSet : colorSet[index],
          backgroundColor: type === 'doughnut' || type === 'pie' || type === 'barColoured' ? colorSet : colorSet[index],
          barThickness: 30,
          minBarThickness: 5,
          maxBarThickness: 50,
          pointRadius: 2,
          pointHoverRadius: 4,
        }
      }),
    }
  }

  const mappedChartData = mapChartData(data)

  if (mappedChartData.datasets.length === 0 || !ALLOWED_CHART_TYPE.includes(type)) {
    return <></>
  }

  return (
    <div>
      {data?.chartName && <Headings variant="h4" content={data?.chartName} />}
      <div className={clsx('', applyMinWidth && 'overflow-x-auto')}>
        <div className={clsx('', applyMinWidth && 'relative min-w-[44rem]')}>
          <ReactChart
            type={type === 'barStacked' || type === 'barColoured' ? 'bar' : (type as keyof ChartTypeRegistry)}
            data={mapChartData(data)}
            options={options}
            ref={chartRef}
          />
        </div>
      </div>
      {data?.disclaimer && <Paragraph content={data?.disclaimer} classes="!text-sm mt-32" />}
    </div>
  )
}

export default Charts
